<script setup lang="ts">

import FormationHeader from '~/components/formation/FormationHeader.vue'
import FormationProgram from '~/components/formation/FormationProgram.vue'
import FormationInfos from '~/components/formation/FormationInfos.vue'


const route = useRoute()
const { getFormationBySlug } = useFormations()
const { setFormationSeo } = useSeo()
const formation = await getFormationBySlug(route.params.slug as string)

if (!formation) {
  throw createError({ statusCode: 404, statusMessage: 'Formation non trouvée' })
}

// Set SEO using the composable
await setFormationSeo({
  title: formation.title,
  description: formation.description,
  metaTitle: formation.metaTitle,
  metaDescription: formation.metaDescription,
  keywords: formation.keywords,
  ogImage: formation.ogImage,
  image: formation.image
})

const { siteUrl, setStructuredData } = useStructuredData()
setStructuredData(buildCourseSchema(siteUrl, {
  title: formation.title,
  description: formation.description,
  url: buildCanonicalUrl(siteUrl, route.path)
}))
</script>

<template>
  <div v-if="formation">
    <UiPageHero 
      :title="formation.title"
      :description="formation.fullDescription"
      :image="formation.image"
      height="screen"
      align="center"
    >
      <template #badge>
        <div class="flex flex-wrap items-center justify-center gap-3">
          <UiBadge variant="primary">
            {{ formation.level }}
          </UiBadge>
          <UiBadge variant="default">
            {{ formation.duration }}
          </UiBadge>
          <UiBadge 
            v-if="formation.isRncp !== undefined"
            :variant="formation.isRncp ? 'info' : 'warning'"
          >
            {{ formation.isRncp ? 'Certification reconnue RNCP' : 'Certification OXO' }}
          </UiBadge>
        </div>
      </template>

      <template #actions>
        <dl
          v-if="formation.stats && formation.stats.length > 0"
          class="flex flex-wrap justify-center gap-12 bg-white/5 backdrop-blur-xs p-8 rounded-3xl border border-white/10 w-fit mx-auto"
        >
          <div v-for="(stat, index) in (formation.stats || [])" :key="index" class="text-center group">
            <dt class="text-[10px] font-black uppercase tracking-[0.2em] text-foreground-dim order-2">{{ stat.label }}</dt>
            <dd class="text-4xl font-black text-foreground mb-2 group-hover:text-oxo-red transition-colors order-1">{{ stat.value }}</dd>
          </div>
        </dl>

        <div class="mt-8 flex flex-wrap items-center justify-center gap-4">
          <AppButton to="/formations" variant="outline" size="md">
            <i class="fas fa-arrow-left mr-2" aria-hidden="true"></i>
            Retour aux formations
          </AppButton>
          <AppButton
            v-if="formation.isRncp && formation.rncpLink"
            :href="formation.rncpLink"
            target="_blank"
            rel="noopener noreferrer"
            variant="secondary"
            size="md"
          >
            <i class="fas fa-award mr-2" aria-hidden="true"></i>
            Consulter la fiche RNCP
            <template #icon>
              <i class="fas fa-external-link-alt" aria-hidden="true"></i>
            </template>
          </AppButton>
        </div>
      </template>
    </UiPageHero>

    
    <FormationInfos :infos="formation.infos || {}" />

    <FormationProgram :program="formation.program || []" />

    <!-- Formation Video Section -->
    <section v-if="formation.videoUrl" class="py-section-py bg-background text-foreground relative overflow-hidden transition-colors duration-300">
      <div class="container mx-auto px-container-px relative z-10">
        <div class="mb-12 text-center">
          <UiSectionHeader
            titlePrefix="Découvrez"
            titleHighlight="LA FORMATION"
            subtitle="Présentation vidéo de la formation et de son programme."
          />
        </div>
        <div class="max-w-5xl mx-auto">
          <UiVideoPlayer :src="formation.videoUrl" :controls="true" />
        </div>
      </div>
    </section>

    <!-- Tuition Fees Section -->
    <TuitionFees />
    
    <AppCTA
      title="Prêt à"
      titleHighlight="LANCER VOTRE CARRIÈRE ?"
      subtitle="Rejoignez la prochaine promotion et transformez votre passion en métier."
      buttonText="Candidater maintenant"
    >
      <AppDiscoverLinks />
    </AppCTA>
  </div>
</template>
